import * as React from 'react'
import {Box, TextField, InputAdornment, FormControl, Select, MenuItem, Button, Typography} from '@mui/material'
import {LoadingButton} from '@mui/lab'
import SearchIcon from '@mui/icons-material/Search'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import {useSnackbar} from 'notistack'
import {filterBarSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {PRIMARY_BLUE, SUBTLE, NAVY, BORDER, WHITE, withOpacity} from '../../../styles/foundation/colors'
import DateRangePicker from '../../DateRangePicker'
import {COURIERS, STATUS, TABS} from './data/reference'
import {nowDate} from './data/clock'
import {downloadDisputeExport} from './exportDisputes'

// Each option names the COLUMN it orders plus the direction, so the merchant
// can connect the sort to something visible in the grid. The old labels named
// three different things three different ways:
//
//   "Urgency (soonest deadline)" — "Urgency" is an internal level enum
//     (crit/warn/ok, see format.js), and the parenthetical was doing all the
//     real work. Worse, the Urgency FILTER sits two controls to the left, so
//     one word meant two unrelated things on the same row.
//   "Highest excess amount" — "excess amount" is the field name; the column it
//     sorts is headed "Excess freight". Same number, two names.
//   "Newest first" — newest what. It sorts on createdAt, which this module
//     already calls "raised" everywhere else (RecoveryAnalytics' "Raised vs.
//     recovered by week", actions.js's "Dispute raised for AWB ..."), so that
//     is the word reused here rather than a fourth one invented.
//
// `tabs` scopes an option to the tabs where it means something. Deadline counts
// down to auto-accept, which can only happen while the merchant still has a
// decision to make — so on In Dispute and Resolved it sorts by a clock that has
// already stopped, exactly the reason the Deadline COLUMN is dropped on those
// tabs too (see DisputeGrid's columnsForTab).
const SORTS = [
  // Direction dropped from the labels on request, for a cleaner control. Each
  // sort only ever runs one way, so the qualifier was never a choice the reader
  // had to make -- but it did tell them which way, so the aria-label on the
  // control below now carries "sorted by X" and each option keeps its direction
  // in `hint` for anywhere that has room to say it.
  {id: 'urgency', label: 'Deadline', hint: 'soonest first', tabs: ['action']},
  {id: 'excess', label: 'Excess freight', hint: 'highest first'},
  {id: 'newest', label: 'Date raised', hint: 'newest first'},
]

/**
 * The sort options valid on a tab. An option with no `tabs` list is valid
 * everywhere. Exported because index.js needs the same answer to coerce a sort
 * that a tab change has just invalidated — if the two disagreed, the dropdown
 * would render blank while the rows stayed in an order nothing on screen names.
 */
export function sortsForTab(tab) {
  return SORTS.filter((s) => !s.tabs || s.tabs.includes(tab))
}

/** The fallback when a tab change invalidates the active sort. */
export const DEFAULT_SORT = 'excess'

// The two backlog views the "View" segmented control switches between — see
// combined-bridge.js's VIEW_DEFS in the offline mockup, which this mirrors
// label- and hint-for-hint. "By product" never re-queries: index.js mounts
// SkuIntelligence (its grouping already lives in state/analytics.js) over
// the exact same tab/filter-scoped `rows` the grid renders, so the two views
// can never disagree about what is in the queue.
const VIEW_DEFS = [
  {id: 'ship', label: 'Ledger view', hint: 'One line per shipment, soonest deadline first. Dispute or View on a line opens the full record.'},
  {id: 'product', label: 'By product', hint: 'The same rows grouped by SKU, because couriers repeat the same mistake on the same product.'},
]

// fontSize on the root is NOT enough: MUI gives `.MuiInputBase-input` and
// `.MuiSelect-select` their own font-size (1rem = 16px), which wins over
// inheritance. Without these two descendant selectors the search field and all
// three dropdowns render at 16px — the largest text on the page after the KPI
// numbers, and a third bigger than everything around them. Measured, not
// guessed: see the harness at /__weight-harness.
const fieldSx = {
  fontFamily: FONT,
  fontSize: SIZE['2xs'],
  minWidth: 150,
  '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE['2xs']},
  '& .MuiSelect-select': {fontFamily: FONT, fontSize: SIZE['2xs']},
}
// A Select's dropdown list renders in a portal attached to document.body, so
// it is outside both fieldSx's reach and the page container's zoom. Left alone
// its items take theme.typography.body1's 1rem and render at 16px — the same
// MUI leak fieldSx fixes for the closed control, one level further out.
const selectMenuProps = {
  PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}},
}

const viewLabelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: SUBTLE}

/** The filters the form edits; `q` and `urgency` stay settable from elsewhere (KPI cards) but have no field. */
const FORM_KEYS = ['awbs', 'status', 'courier', 'sku', 'minExcessKg', 'minExcessAmount']

/**
 * Search, courier and sort are the whole filter set — the three a
 * merchant reaches for daily — plus the date range on its own row above,
 * visible so they can always see which window they are looking at.
 *
 * Download closes the row. It exports every discrepancy matching whatever
 * is set here — `rows` is the full filtered set across every page, not the
 * page on screen and not the selection — so the file and the screen always
 * describe the same set.
 *
 * The optional "View" segmented control (`view`/`onViewChange`) — the mockup's
 * cmb-viewbar — renders above that, on its own row, only when the caller
 * passes it (index.js does this for the Action Required tab only, mirroring
 * the offline mockup's own scoping).
 */
export default function FilterBar({
  filters, onChange, onApply, onReset, sort, onSortChange,
  view, onViewChange, range, onRangeChange, tab, rows, account, fetchAllRows,
}) {
  const {enqueueSnackbar} = useSnackbar()
  const sortOptions = sortsForTab(tab)
  const hasActiveFilter = Object.values(filters).some(Boolean)

  // The form's own copy, applied on Filter. Re-seeded whenever the applied filters change from outside
  // (Clear filters, a KPI card), so the fields always show what the list is actually filtered by.
  const draftFrom = (f) => FORM_KEYS.reduce((o, k) => ({...o, [k]: (f && f[k]) || ''}), {})
  const [draft, setDraft] = React.useState(() => draftFrom(filters))
  React.useEffect(() => { setDraft(draftFrom(filters)) }, [filters]) // eslint-disable-line react-hooks/exhaustive-deps
  const setDraftField = (k, v) => setDraft((d) => ({...d, [k]: v}))
  const draftChanged = FORM_KEYS.some((k) => (draft[k] || '') !== ((filters && filters[k]) || ''))
  const applyDraft = () => {
    if (onApply) onApply(draft)
    else FORM_KEYS.forEach((k) => onChange(k, draft[k]))
  }
  const tabDef = TABS.find((t) => t.id === tab)
  const statusOptions = (tabDef && tabDef.statuses) || []
  const [downloading, setDownloading] = React.useState(false)
  const showViewBar = !!(view && onViewChange)
  const activeHint = showViewBar ? (VIEW_DEFS.find((v) => v.id === view) || {}).hint : ''

  // The shared DateRangePicker (src/app/components/DateRangePicker.js) works
  // in react-date-range's own shape — an array holding one
  // {startDate, endDate, key} selection — never the preset string/object
  // `range` is otherwise stored as. Converting at this boundary, rather than
  // changing what useDisputeState or state/query.js's inRange accept, keeps
  // that shape a FilterBar-local wiring detail.
  const pickerValue = React.useMemo(() => {
    // A relative preset ('90d') is resolved to real dates so the control SHOWS the window in force.
    // It used to fall through to the empty placeholder, which was harmless while the default was
    // 'all' and a lie the moment a live mount started defaulting to 90 days: the list was filtered
    // and the only control that could say so read "Select Date Range".
    if (typeof range === 'string' && range !== 'all') {
      const days = {'7d': 7, '30d': 30, '90d': 90}[range]
      if (days) {
        return [{startDate: new Date(Date.now() - days * 86400000), endDate: new Date(), key: 'selection'}]
      }
    }
    const sel = range && typeof range === 'object' ? range : {}
    return [{startDate: sel.startDate || null, endDate: sel.endDate || null, key: 'selection'}]
  }, [range])

  const handleDateRangeChange = (next) => {
    if (!onRangeChange) return
    const sel = (next && next[0]) || {}
    onRangeChange({startDate: sel.startDate || null, endDate: sel.endDate || null})
  }

  const handleViewKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const ids = VIEW_DEFS.map((v) => v.id)
    const cur = ids.indexOf(view)
    const next = ids[(cur + (e.key === 'ArrowRight' ? 1 : ids.length - 1)) % ids.length]
    onViewChange(next)
  }

  // Building and writing the workbook is synchronous and blocks the main
  // thread, so setting `downloading` and calling straight into it would paint
  // the spinner only after the work was already done. The awaited timeout
  // yields one frame first, which is what makes the disabled/progress state
  // actually visible on the sets large enough to need it.
  const handleDownload = async () => {
    if (downloading) return
    // Live: every row for the tab, filters and dates comes from the server (28 Oct), not just the page on
    // screen. Seeded: `rows` already is the whole filtered set.
    if (!fetchAllRows && (rows || []).length === 0) {
      enqueueSnackbar('Nothing to download — no discrepancies match the current filters.', {variant: 'warning'})
      return
    }
    setDownloading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 0))
      const fetched = fetchAllRows ? await fetchAllRows() : null
      const matching = fetched ? fetched.rows : rows || []
      if (matching.length === 0) {
        enqueueSnackbar('Nothing to download — no discrepancies match the current filters.', {variant: 'warning'})
        return
      }
      const {count} = downloadDisputeExport({rows: matching, account, range})
      enqueueSnackbar(fetched && fetched.capped
        ? `Downloaded the first ${count} discrepancies (the limit). Narrow the dates for the rest.`
        : `Downloaded ${count} ${count === 1 ? 'discrepancy' : 'discrepancies'}`, {variant: fetched && fetched.capped ? 'warning' : 'success'})
    } catch (err) {
      enqueueSnackbar((err && err.message) || 'Download failed', {variant: 'error'})
    } finally {
      setDownloading(false)
    }
  }

  // Padding trimmed locally, not in styles/patterns — filterBarSx is shared by
  // every filter card in the panel. This card was the tallest band above the
  // queue at 141px; the page was asked to fit at 100% what it previously only
  // fit at 80%, and chrome is the first thing to give back.
  return (
    <Box sx={{...filterBarSx, p: 1.75, mb: 1.5}}>
      {/* One top row carrying the View control (when the caller passes it) on
          the left and the date range on the right, in what was blank space.
          Rendered when EITHER is present: the View bar is Action-Required-only,
          but the date range applies to every grid tab, so gating the whole row
          on showViewBar would hide the picker on In Dispute and Resolved. */}
      {(showViewBar || onRangeChange) && (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', pb: 1.25, mb: 1.25, borderBottom: `1px solid ${BORDER}`}}>
          {showViewBar && (
            <>
              <Typography sx={{...viewLabelSx, textTransform: 'uppercase', letterSpacing: '0.04em'}}>View</Typography>

              <Box
                role='tablist'
                aria-label='Backlog view'
                onKeyDown={handleViewKeyDown}
                sx={{display: 'inline-flex', bgcolor: withOpacity(SUBTLE, 0.1), borderRadius: '10px', p: '3px', gap: '2px'}}
              >
                {VIEW_DEFS.map((v) => {
                  const active = view === v.id
                  return (
                    <Box
                      key={v.id}
                      component='button'
                      type='button'
                      role='tab'
                      aria-selected={active}
                      tabIndex={active ? 0 : -1}
                      onClick={() => onViewChange(v.id)}
                      sx={{
                        fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                        border: 0, borderRadius: '8px', px: 1.5, py: 0.5, cursor: 'pointer',
                        bgcolor: active ? WHITE : 'transparent',
                        color: active ? NAVY : SUBTLE,
                        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                        '&:focus-visible': {outline: `2px solid ${PRIMARY_BLUE}`, outlineOffset: '1px'},
                      }}
                    >
                      {v.label}
                    </Box>
                  )
                })}
              </Box>

              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, minWidth: 0}}>
                {activeHint}
              </Typography>
            </>
          )}

          {/* Pushed to the right end of this row with ml:auto, into what was
              blank space beside the View hint. Width is capped here because the
              shared DateRangePicker hardcodes width:100% on its own TextField
              (src/app/components/DateRangePicker.js) — the wrapper is the only
              place its size can be controlled without editing a component the
              whole app shares. */}
          {onRangeChange && (
            <Box sx={{ml: 'auto', width: 200, flexShrink: 0}}>
              {/* The popover defaults to a two-month calendar plus a full
                  preset rail plus a date-display header, which is far larger
                  than a filter-bar control warrants. Everything below is passed
                  through DateRangePicker's `...other` onto the underlying
                  react-date-range picker, and lands AFTER that component's own
                  defaults — so this instance shrinks without editing a
                  component the whole app shares.

                  months=1        one calendar instead of two, which is the bulk
                                  of the width.
                  showDateDisplay the two large boxes reading "Early" and
                    = false       "Continuous" are react-date-range's own
                                  placeholders for an unset start/end. They say
                                  nothing, and the trigger field already shows
                                  the chosen range.
                  inputRanges=[]  drops the "— days up to today" / "— days
                                  starting today" number fields. The named
                                  presets above them stay, and do the same job
                                  without typing. */}
              <DateRangePicker
                showTextBox
                label='Select Date Range'
                defaultRange={pickerValue}
                maxDate={nowDate()}
                onChange={handleDateRangeChange}
                months={1}
                showDateDisplay={false}
                inputRanges={[]}
              />
            </Box>
          )}
        </Box>
      )}

      <Box
        component='form'
        onSubmit={(e) => { e.preventDefault(); applyDraft() }}
        sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center'}}
      >
        {/* Filled in, then applied together with Filter — one request, not one per keystroke or paste. */}
        <TextField
          size='small'
          multiline
          maxRows={3}
          placeholder='AWB(s) — paste many'
          value={draft.awbs}
          onChange={(e) => setDraftField('awbs', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{fontSize: 16, color: SUBTLE}} />
              </InputAdornment>
            ),
          }}
          inputProps={{'aria-label': 'AWBs'}}
          sx={{...fieldSx, minWidth: 220}}
        />

        {statusOptions.length > 1 && (
          <FormControl size='small' sx={fieldSx}>
            <Select displayEmpty MenuProps={selectMenuProps} value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} inputProps={{'aria-label': 'Status'}}>
              <MenuItem value=''>All statuses</MenuItem>
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>{(STATUS[s] && STATUS[s].label) || s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size='small' sx={fieldSx}>
          <Select displayEmpty MenuProps={selectMenuProps} value={draft.courier} onChange={(e) => setDraftField('courier', e.target.value)} inputProps={{'aria-label': 'Courier'}}>
            <MenuItem value=''>All couriers</MenuItem>
            {COURIERS.map((c) => (
              <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField size='small' placeholder='SKU' value={draft.sku} onChange={(e) => setDraftField('sku', e.target.value)}
          inputProps={{'aria-label': 'SKU'}} sx={{...fieldSx, minWidth: 110, width: 120}} />
        <TextField size='small' type='number' placeholder='Excess ≥ kg' value={draft.minExcessKg} onChange={(e) => setDraftField('minExcessKg', e.target.value)}
          inputProps={{'aria-label': 'Minimum excess weight in kg', min: 0, step: '0.1'}} sx={{...fieldSx, minWidth: 100, width: 110}} />
        <TextField size='small' type='number' placeholder='Excess ≥ ₹' value={draft.minExcessAmount} onChange={(e) => setDraftField('minExcessAmount', e.target.value)}
          inputProps={{'aria-label': 'Minimum excess freight in rupees', min: 0, step: '1'}} sx={{...fieldSx, minWidth: 100, width: 110}} />

        <Button
          type='submit'
          size='small'
          variant='contained'
          disabled={!draftChanged}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', bgcolor: PRIMARY_BLUE, boxShadow: 'none', whiteSpace: 'nowrap'}}
        >
          Filter
        </Button>


        {/* No visible label — the row is width-constrained and the option text
            reads as a sort order on its own. Screen readers get one here, which
            they otherwise would not: the control announces only its current
            value, giving no clue that it sorts rather than filters. */}
        <FormControl size='small' sx={{...fieldSx, ml: 'auto'}}>
          <Select
            MenuProps={selectMenuProps}
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            inputProps={{'aria-label': 'Sort order'}}
          >
            {sortOptions.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Exports every row the filters currently match — the whole set,
            across every page, not the page on screen. `whiteSpace: nowrap`
            because this row wraps, and a two-line button would set the row's
            height for every other control on it. */}
        <LoadingButton
          size='small'
          variant='outlined'
          loading={downloading}
          loadingPosition='start'
          startIcon={<FileDownloadOutlinedIcon sx={{fontSize: 16}} />}
          onClick={handleDownload}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', borderColor: BORDER, color: NAVY, whiteSpace: 'nowrap'}}
        >
          Download
        </LoadingButton>

        {hasActiveFilter && (
          <Button
            size='small'
            onClick={onReset}
            sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}
          >
            Clear filters
          </Button>
        )}
      </Box>
    </Box>
  )
}
